// Kanemori Bulwark APC: slab-sided armoured carrier. Numbers in hd px (see docs/CAR_ART.md).
// Lower hull (vertical sides, raked glacis) under a tapered superstructure (angled side plates, raked front, slit windows).
import { K, dk, lt } from '../hd-kit';
import { registerCarModel, type CarModel, type V3, type Painter } from '../car-drawn';

const HW = 14, ZH = 10;            // hull half width, hull deck height
const TW = 10, ZT = 20;            // superstructure top half width and roof height
const UB = [-26, 22], UT = [-24, 14]; // superstructure u range at the deck and at the roof
const AX = [19, 0, -19], WR = 5;
const slope = (z: number) => HW - (HW - TW) * (z - ZH) / (ZT - ZH); // v of the side plate at height z
const frontU = (z: number) => UB[1] + (UT[1] - UB[1]) * (z - ZH) / (ZT - ZH);
const rearU = (z: number) => UB[0] + (UT[0] - UB[0]) * (z - ZH) / (ZT - ZH);

function build(P: Painter) {
  const { fill, body, wrecked } = P, tone = P.tone;
  const lamp = (on: string, off: string) => (P.lampOn ? on : off);
  const nSide = (s: number): V3 => [0, s, 0];
  const nPlate = (s: number): V3 => [0, s * .96, .29];
  // --- lower hull sides, with arches, seams and bolts
  for (const s of [1, -1]) {
    const v = s * HW;
    const wall: V3[] = ([[-28, 3], [28, 3], [28, 7], [23, ZH], [-28, ZH]] as [number, number][]).map(p => [p[0], v, p[1]] as V3);
    P.face(wall, nSide(s), tone(nSide(s)), () => {
      fill(P.sideRect(v, -26, 26, 3, 4.4), K.metalLo); // skirt
      for (const u of P.axles) fill(P.disc(u, s * (HW + P.flare), WR, WR + .9).map(p => [p[0], p[1], Math.min(ZH + .4, Math.max(3, p[2]))] as V3), K.ink);
      const seam = dk(body, .8), hi = lt(body, .3);
      for (const u of [-9.5, 9.5]) fill(P.sideRect(v, u - .35, u + .35, 4.4, ZH), seam); // panel seams between the axles
      fill(P.sideRect(v, -27, 22, ZH - 1.2, ZH - .5), hi); // top edge highlight
      for (const u of [-24.5, -13, -5, 5, 13, 25]) for (const z of [5.6, 8.6]) fill(P.sideRect(v, u - .4, u + .4, z - .4, z + .4), K.metalHi); // bolts
      fill(P.sideRect(v, 24, 27, 4.4, 6), wrecked ? dk(K.amber, .6) : K.amber); // tow shackle plate on the nose
      if (wrecked) { // a hull plate blown off (dark frame over bare inner metal) and soot streaks rising from it
        fill(P.sideRect(v, 1, 8, 4.8, 9.2), K.ink); fill(P.sideRect(v, 1.8, 7.2, 5.4, 8.6), K.metalLo); fill(P.sideRect(v, 3, 4, 5.8, 8.2), K.metal);
        fill([[-14, v, 4.4], [-6, v, 4.4], [-8, v, ZH - .6], [-15, v, ZH - .6]], K.woodLo); fill([[-12, v, 5], [-9, v, 5], [-10, v, 8.4], [-12.4, v, 8.4]], K.ink);
      }
    });
  }
  // --- front: bumper block, then glacis
  const nose: V3[] = P.endRect(28, -HW, HW, 3, 7);
  P.face(nose, [1, 0, 0], tone([1, 0, 0]), () => {
    fill(P.endRect(28, -HW, HW, 3, 4.2), K.metalLo); fill(P.endRect(28, -HW, HW, 4.2, 4.7), K.metalHi); // bumper with a lit top edge
    for (let v = -12; v <= 12; v += 6) fill(P.endRect(28, v - .5, v + .5, 3.4, 4.1), K.white); // bumper bolts
    // hazard chevrons: wide diagonal amber bands (one band = 3 units across so they stay readable)
    fill(P.endRect(28, -8.4, 8.4, 4.2, 7), K.ink); // black band under the amber so chevrons keep their edge at any angle
    for (let v = -8; v < 7.5; v += 4) {
      fill([[28, v, 4.4], [28, v + 2, 4.4], [28, v + 4, 6.8], [28, v + 2, 6.8]], wrecked ? dk(K.amber, .6) : K.amber);
    }
    for (const s of [1, -1]) { // recessed lamps
      fill(P.endRect(28, s * 9.4, s * 12.8, 4.5, 6.8), K.ink);
      fill(P.endRect(28, s * 9.9, s * 12.3, 4.9, 6.4), lamp(K.warm, K.metalLo));
      if (P.lampOn) fill(P.endRect(28, s * 10.4, s * 11.8, 5.3, 6.1), K.warmHi);
    }
  });
  const gl: V3[] = [[28, -HW, 7], [28, HW, 7], [23, HW, ZH], [23, -HW, ZH]];
  P.face(gl, [.5, 0, .86], tone([.5, 0, .86]), () => {
    fill([[27.4, -5, 7.2], [27.4, 5, 7.2], [25, 5, 9], [25, -5, 9]], lt(K.metalHi, .15)); // lit grille frame
    fill([[27, -4, 7.4], [27, 4, 7.4], [25.3, 4, 8.8], [25.3, -4, 8.8]], K.ink); // grille
    for (let v = -3; v < 4; v += 2) fill([[26.6, v, 7.6], [26.6, v + 1, 7.6], [25.7, v + 1, 8.5], [25.7, v, 8.5]], K.metalHi);
  });
  // deck between glacis and superstructure (nose top)
  P.face([[23, -HW, ZH], [23, HW, ZH], [UB[1], HW, ZH], [UB[1], -HW, ZH]], [0, 0, 1], lt(body, .08), () => {
    fill([[22.9, -HW + 1, ZH + .01], [22.9, HW - 1, ZH + .01], [22.2, HW - 1, ZH + .01], [22.2, -HW + 1, ZH + .01]], lt(body, .3));
  });
  // --- rear face
  P.face(P.endRect(-28, -HW, HW, 3, ZH - 1), [-1, 0, 0], tone([-1, 0, 0]), () => {
    fill(P.endRect(-28, -HW, HW, 3, 4.2), K.metalLo);
    fill(P.endRect(-28, -7, 7, 5, ZH - 2.2), dk(body, .25)); // rear door
    fill(P.endRect(-28, -.3, .3, 5, ZH - 2.2), K.ink); fill(P.endRect(-28, -5.5, -1, 7.4, 8.4), wrecked ? K.glassLo : K.glassHi); fill(P.endRect(-28, 1, 5.5, 7.4, 8.4), wrecked ? K.glassLo : K.glassHi); // lit vision slits in the rear doors
    fill(P.endRect(-28, -HW, HW, 4.2, 4.7), K.metalHi);
    fill(P.endRect(-28, 4.6, 5.6, 6.6, 7.6), K.metalHi); fill(P.endRect(-28, -5.6, -4.6, 6.6, 7.6), K.metalHi); // door handles
    for (const s of [1, -1]) {
      fill(P.endRect(-28, s * 9, s * 12.8, 4.8, 7), K.ink);
      fill(P.endRect(-28, s * 9.5, s * 12.3, 5.2, 6.6), lamp(K.red, K.metalLo));
      if (P.lampOn) fill(P.endRect(-28, s * 10, s * 11.6, 5.8, 6.2), K.pink);
    }
  });
  // --- superstructure: angled side plates with slit windows
  for (const s of [1, -1]) {
    const n = nPlate(s);
    const quad: V3[] = [[UB[0], s * HW, ZH], [UB[1], s * HW, ZH], [UT[1], s * TW, ZT], [UT[0], s * TW, ZT]];
    P.face(quad, n, tone(n), () => {
      const pt = (u: number, z: number): V3 => [u, s * slope(z), z];
      for (const u of [-18, -8, 2, 11]) { // slit windows
        const slit: V3[] = [pt(u, 14.2), pt(u + 6, 14.2), pt(u + 6, 16.4), pt(u, 16.4)];
        P.fill1(slit, K.ink); const inner: V3[] = [pt(u + .6, 14.8), pt(u + 5.4, 14.8), pt(u + 5.4, 15.8), pt(u + .6, 15.8)];
        P.fill1(inner, wrecked ? P.glassTone(n) : K.glassHi); // a slit always keeps at least one lit pixel
      }
      fill([pt(-24, 12.2), pt(20, 12.2), pt(20, 12.8), pt(-24, 12.8)], lt(body, .3)); // plate line
      if (wrecked) { fill([pt(-20, 12.8), pt(-6, 13), pt(-9, 19.4), pt(-21, 19)], K.woodLo); fill([pt(-17, 13.4), pt(-10, 13.4), pt(-12, 18.4), pt(-17, 18)], K.ink); fill([pt(4, 16.6), pt(12, 17), pt(10, 19.6), pt(5, 19.4)], K.woodLo); } // scorched plates
      for (const u of [-23, -13, -3, 7, 17]) { const b = pt(u, 18.2); fill([[u - .4, b[1], b[2] - .4], [u + .4, b[1], b[2] - .4], [u + .4, b[1], b[2] + .4], [u - .4, b[1], b[2] + .4]], K.metalHi); }
    });
  }
  // raked front plate with the driver's slit
  const nf: V3 = [.78, 0, .62];
  P.face([[UB[1], -HW, ZH], [UB[1], HW, ZH], [UT[1], TW, ZT], [UT[1], -TW, ZT]], nf, tone(nf), () => {
    const q = (z: number, v: number): V3 => [frontU(z), v, z];
    P.fill1([q(14.4, -8), q(14.4, 8), q(16.8, 8), q(16.8, -8)], K.ink);
    P.fill1([q(14.9, -7.4), q(14.9, 7.4), q(16.2, 7.4), q(16.2, -7.4)], wrecked ? P.glassTone(nf) : K.glassHi); // lit vision block
    for (const s of [1, -1]) { fill([q(11.2, s * 10.5), q(11.2, s * 12), q(12.6, s * 12), q(12.6, s * 10.5)], K.ink); fill([q(11.5, s * 10.8), q(11.5, s * 11.7), q(12.3, s * 11.7), q(12.3, s * 10.8)], lamp(K.warm, K.metalLo)); }
    fill([q(18.4, -9), q(18.4, 9), q(18.9, 9), q(18.9, -9)], lt(body, .3));
  });
  const nr: V3 = [-.78, 0, .62];
  P.face([[UB[0], -HW, ZH], [UB[0], HW, ZH], [UT[0], TW, ZT], [UT[0], -TW, ZT]], nr, tone(nr), () => {
    const q = (z: number, v: number): V3 => [rearU(z), v, z];
    for (const s of [1, -1]) { fill([q(13, s * 3), q(13, s * 8), q(17, s * 8), q(17, s * 3)], dk(body, .25)); fill([q(14.4, s * 4), q(14.4, s * 7), q(15.6, s * 7), q(15.6, s * 4)], K.ink); }
  });
  // roof: hatch, vent grilles, bolts
  P.face([[UT[0], -TW, ZT], [UT[0], TW, ZT], [UT[1], TW, ZT], [UT[1], -TW, ZT]], [0, 0, 1], lt(body, .22), () => {
    const z = ZT + .02;
    fill([[-22, -TW + .8, z], [12, -TW + .8, z], [12, -TW + 1.8, z], [-22, -TW + 1.8, z]], lt(body, .5)); // north edge highlight
    fill([[-4, -6, z], [3, -6, z], [3, 0, z], [-4, 0, z]], dk(body, .35)); // hatch ring
    if (wrecked) { // the hatch is blown open: a black hole, the lid flung onto the roof beside it, scorch around
      fill([[-6, -8, z], [6, -8.6, z], [5, 2, z], [-5, 1.4, z]], K.woodLo); fill([[-3.2, -5.2, z], [2.2, -5.2, z], [2.2, -.8, z], [-3.2, -.8, z]], K.ink);
      fill([[7, -8, z + .01], [11.6, -7.4, z + .01], [11, -3, z + .01], [6.4, -3.6, z + .01]], lt(body, .1)); fill([[8.6, -6, z + .02], [9.6, -6, z + .02], [9.6, -5.2, z + .02], [8.6, -5.2, z + .02]], K.metalHi);
    } else {
      fill([[-3.2, -5.2, z], [2.2, -5.2, z], [2.2, -.8, z], [-3.2, -.8, z]], lt(body, .1));
      fill([[-.3, -3.4, z], [.9, -3.4, z], [.9, -2.6, z], [-.3, -2.6, z]], K.metalHi); // hatch handle
    }
    for (const u of [-21, -17, -13]) fill([[u, 1.5, z], [u + 2, 1.5, z], [u + 2, 8, z], [u, 8, z]], K.ink); // vent grille slots (wide bars)
    for (const u of [-21, -17, -13]) fill([[u + .5, 1.8, z], [u + 1.5, 1.8, z], [u + 1.5, 7.6, z], [u + .5, 7.6, z]], K.metalLo);
    fill([[6, 2, z], [11, 2, z], [11, 7.5, z], [6, 7.5, z]], dk(body, .3)); // roof box
    fill([[6.6, 2.6, z], [10.4, 2.6, z], [10.4, 6.9, z], [6.6, 6.9, z]], K.metalLo);
  });
}

export const armored: CarModel = {
  body: [[-28, 3], [28, 3], [28, 10], [-28, 10]], hw: HW, axles: AX, wheelR: WR, archTop: ZH,
  bodyColor: () => dk(K.slate, .05),
  custom: build,
};
registerCarModel('armored', armored);
